import type {ReactElement} from 'react';
import {useContext} from 'react';
import {WIZARD_HEADER_CLASS} from '@Libraries/social-media-wizard-library';
import {BLURABLE_CLASS_IN_AI_CAPTION_MODE, SocialMediaWizardContentSize, SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import {Text, TextTag} from '@Components/text';
import {useWizardStep} from '@Components/social-media/social-media.hooks';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {isUserLoggedIn} from '@Libraries/user.library';
import styles from './social-media-wizard-content.module.scss';
import type {SocialMediaWizardContentProps} from './social-media-wizard-content.types';

export function SocialMediaWizardContent({
  heading,
  mainContent,
  previewContent,
  size = SocialMediaWizardContentSize.XLARGE,
}: SocialMediaWizardContentProps): ReactElement {
  const isWeb = size === SocialMediaWizardContentSize.XLARGE || size === SocialMediaWizardContentSize.LARGE || size === SocialMediaWizardContentSize.MEDIUM;
  const wizardStep = useWizardStep();
  const {isSmallScreen, isExtraSmallScreen} = useContext(SocialMediaWizardContainerQueriesContext);
  const isLoggedIn = isUserLoggedIn();

  /**
   * Whether to use the web (multi-column) layout rather than the mobile (single-column) one. Both layouts
   * render the SAME element tree down to mainContent/previewContent, differing only in classNames — so
   * crossing the breakpoint re-renders in place instead of unmounting and remounting the content subtree.
   * A remount would re-run the children's mount effects (e.g. PublishingPost's publishing-status init),
   * which is what reset an already-published post back to "Publishing" on resize.
   */
  const isWebLayout = (): boolean => {
    return isWeb || wizardStep === SocialMediaWizardStep.SELECT_DESIGN;
  };

  const getContentContainerStyles = (): string => {
    if (size === SocialMediaWizardContentSize.XSMALL) {
      return styles.xsmall;
    }

    if (size === SocialMediaWizardContentSize.SMALL) {
      return styles.small;
    }

    if (size === SocialMediaWizardContentSize.MEDIUM) {
      return styles.medium;
    }

    if (size === SocialMediaWizardContentSize.LARGE) {
      return styles.large;
    }

    return styles.xlarge;
  };

  const getWizardHeaderClasses = (): string => {
    let topSpacingClass = '';
    if (size === SocialMediaWizardContentSize.XSMALL) {
      topSpacingClass = 'spacing-m-t-4';
    }

    return `_full-width ${topSpacingClass} spacing-m-b-6 ${BLURABLE_CLASS_IN_AI_CAPTION_MODE}`;
  };

  const renderWizardHeading = (): ReactElement => {
    return (
      <div className={getWizardHeaderClasses()}>
        <Text tag={TextTag.H1} val={heading} className={`${WIZARD_HEADER_CLASS} ${styles.wizardContentHeading}`} />
      </div>
    );
  };

  const renderHeadingSlot = (): ReactElement | null => {
    if (isWeb) {
      return renderWizardHeading();
    }

    return null;
  };

  const renderStepBody = (): ReactElement => {
    if (!previewContent) {
      return <div className={styles.stepBody}>{mainContent}</div>;
    }

    if (isWebLayout()) {
      return (
        <div className={styles.stepBodyColumns}>
          <div className={styles.stepBodyColumn}>{mainContent}</div>
          <div className={`${styles.stepBodyColumn} ${styles.stepBodyPreviewColumn}`}>{previewContent}</div>
        </div>
      );
    }

    return (
      <div className={styles.stepBody}>
        <div className="_full-width">{mainContent}</div>
      </div>
    );
  };

  const getContentSpacingClasses = (): string => {
    if (!isSmallScreen && !isExtraSmallScreen) {
      return styles.contentTopSpacing;
    }

    if (wizardStep === SocialMediaWizardStep.SELECT_DESIGN) {
      return 'spacing-p-t-4 spacing-p-l-6 spacing-p-r-6';
    }

    return `${styles.mobileContentPadding} spacing-m-t-6`;
  };

  const shouldFlushContentBottom = (): boolean => {
    return wizardStep === SocialMediaWizardStep.SELECT_DESIGN;
  };

  const getContentClasses = (): string => {
    return `
    _full-width
    ${styles.contentContainer}
    ${getContentSpacingClasses()}
    ${shouldFlushContentBottom() ? styles.flushBottom : ''}
    ${isLoggedIn ? '' : '_unpadded-bottom'}
    ${getContentContainerStyles()}
    ${isWebLayout() ? '' : `${styles.mobileWrapper} spacing-p-r-4 spacing-p-l-4 spacing-m-b-5`}
   `;
  };

  return (
    <div className={getContentClasses()}>
      {renderHeadingSlot()}
      {renderStepBody()}
    </div>
  );
}
